import type {SupabaseClient} from '@supabase/supabase-js';
import type {HabiFacts,Persona} from './habiJourney';
export interface HabiFactsInput {
 organizationId:string;userId:string;persona:Persona|null;configurationId:string|null;chatJid:string|null;
 colleagueId:string|null;pendingEmail:string|null;requestNew:boolean;
 permissions:Pick<HabiFacts,'canConnect'|'canInvite'|'canManageAccess'|'canAssign'>;
 uncertainWrite:boolean;
}
export function emptyHabiFacts(input:HabiFactsInput):HabiFacts {
 return {observedAt:0,persona:input.persona,numberConnected:'unknown',selfCanAccess:'unknown',colleagueActive:'unknown',invitationPending:'unknown',colleagueCanAccess:'unknown',conversationSelected:input.chatJid?'yes':'no',assignedToColleague:'unknown',recipientVerified:'unknown',...input.permissions,uncertainWrite:input.uncertainWrite,receiving:input.persona==='invited'};
}
/** Read only, authenticated, scoped. No customer content or secret configuration JSON selected.
 * Pending invitation is matched to an explicit UI choice, never to any unrelated org invitation.
 */
export async function collectHabiFacts(client:SupabaseClient,input:HabiFactsInput,ports:{pendingInvites:()=>Promise<{email:string;status:string;expires_at:string}[]>;verifyRecipient?:()=>Promise<unknown>},now=Date.now()):Promise<{facts:HabiFacts;resolvedColleagueId:string|null}> {
 const facts=emptyHabiFacts(input);let resolvedColleagueId=input.colleagueId;
 const result=()=>({facts,resolvedColleagueId});
 try {
  const identity=await client.auth.getUser();if(identity.error||identity.data.user?.id!==input.userId)return result();
  const actor=await client.from('user_profiles').select('id').eq('organization_id',input.organizationId).eq('id',input.userId).eq('active',true).single();
  if(actor.error||!actor.data)return result();
  let configsQuery=client.from('api_configurations').select('id,user_id,visibility,service_name,session_health_status,webhook_subscribed_at')
   .eq('organization_id',input.organizationId).eq('is_active',true).in('service_name',['waha','whatsapp_cloud','whatsapp_cloud_coex']);
  if(input.configurationId)configsQuery=configsQuery.eq('id',input.configurationId);
  const configs=await configsQuery;
  if(configs.error)return result();
  const connected=(configs.data||[]).filter(c=>c.service_name==='waha'?['healthy','WORKING'].includes(c.session_health_status):!!c.webhook_subscribed_at);
  facts.observedAt=now;facts.numberConnected=connected.length?'yes':'no';facts.numberSelected=input.configurationId?'yes':'no';
  if(!connected.length||!input.configurationId)return result();
  const config=connected[0];
  const acl=await client.from('whatsapp_user_access').select('user_id').eq('organization_id',input.organizationId).eq('configuration_id',input.configurationId);
  if(acl.error)return result();
  const ids=new Set((acl.data||[]).map(row=>row.user_id));
  const allowed=(id:string)=>config.user_id===id||((config.visibility==='global'||!config.user_id)&&(ids.size===0||ids.has(id)));
  facts.selfCanAccess=allowed(input.userId)?'yes':'no';
  facts.colleagueSelected=input.colleagueId||input.pendingEmail||input.requestNew?'yes':'no';
  if(facts.colleagueSelected==='no')return result();
  if(input.colleagueId||input.pendingEmail){
   let q=client.from('user_profiles').select('id').eq('organization_id',input.organizationId).eq('active',true);
   q=input.colleagueId?q.eq('id',input.colleagueId):q.eq('email',input.pendingEmail!.trim().toLowerCase());
   const colleague=await q.maybeSingle();if(colleague.error)return result();
   resolvedColleagueId=colleague.data?.id||null;facts.colleagueActive=resolvedColleagueId?'yes':'no';
  }else facts.colleagueActive='no';
  facts.invitationPending='no';
  if(!resolvedColleagueId){
   if(input.pendingEmail){
    try{facts.invitationPending=(await ports.pendingInvites()).some(i=>i.email.toLowerCase()===input.pendingEmail!.toLowerCase()&&i.status==='pending'&&Date.parse(i.expires_at)>now)?'yes':'no';}
    catch{facts.invitationPending='unknown';}
   }
   return result();
  }
  facts.colleagueCanAccess=allowed(resolvedColleagueId)?'yes':'no';
  if(!input.chatJid)return result();
  // Restrict filter syntax even though selection comes from application state.
  if(!/^[0-9A-Za-z._:-]{1,160}@(c\.us|s\.whatsapp\.net|g\.us|lid)$/.test(input.chatJid)){facts.conversationSelected='unknown';return result();}
  const binding=await client.from('whatsapp_messages').select('id').eq('organization_id',input.organizationId).eq('config_id',input.configurationId)
   .or(`from_jid.eq.${input.chatJid},to_jid.eq.${input.chatJid}`).limit(1);
  if(binding.error||!binding.data?.length){facts.conversationSelected='unknown';return result();}
  const meta=await client.from('whatsapp_chat_meta').select('assigned_operator_id,assigned_at').eq('organization_id',input.organizationId).eq('chat_jid',input.chatJid).maybeSingle();
  if(meta.error)return result();
  const target=facts.receiving?input.userId:resolvedColleagueId;
  facts.assignedToColleague=meta.data?.assigned_operator_id===target?'yes':'no';
  if(facts.assignedToColleague==='yes'&&ports.verifyRecipient){
   try{await ports.verifyRecipient();facts.recipientVerified='yes';}catch{facts.recipientVerified='no';}
  }
  return result();
 }catch{return result();}
}
